import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { FullWidth } from '../../Misc/FullWidth'
import { SummaryCardPublic } from './SummaryCardPublic'

type Props = {
  backgroundColor?: string
  summaries: { metric: string; description: string }[]
  hideAnimation?: boolean
  handleCardClick?: (index: number) => void
  isEditMode?: boolean
  handleCardRemoval?: (index: number) => void
}

export const SummaryBlockPublic = ({
  backgroundColor,
  summaries,
  hideAnimation,
  handleCardClick,
  isEditMode,
  handleCardRemoval,
}: Props) => {
  const targetRef = useRef<HTMLDivElement | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  // IntersectionObserver is used to wait for the block to be on screen before the metric animation begins
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        setIsVisible(entry.isIntersecting)
      },
      { threshold: 1 }
    )
    if (targetRef.current) {
      observer.observe(targetRef.current)
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current)
      }
    }
  }, [])

  const handleClick = (index: number) => {
    if (handleCardClick) {
      handleCardClick(index)
    }
  }

  return (
    <FullWidth backgroundColor={backgroundColor}>
      <SummaryContainer ref={targetRef} isEditMode={isEditMode}>
        {/* filter out summary where the metric and description are empty strings */}
        {summaries
          ?.filter(
            ({ metric, description }) => metric !== '' || description !== ''
          )
          ?.map((summary, index) => (
            <SummaryCardPublic
              backgroundColor={backgroundColor}
              summary={summary}
              isVisible={isVisible}
              hideAnimation={hideAnimation}
              handleCardClick={() => handleClick(index)}
              key={summary.metric}
              handleCardRemoval={() =>
                handleCardRemoval && handleCardRemoval(index)
              }
              index={index}
            />
          ))}
      </SummaryContainer>
    </FullWidth>
  )
}

const SummaryContainer = styled('div')<{ isEditMode?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  max-width: 1058px;
  gap: 36px;

  & > div {
    ${({ isEditMode }) => (isEditMode ? 'cursor: pointer;' : '')}
  }

  &:hover {
    & .delete-button {
      display: ${({ isEditMode }) => (isEditMode ? 'block' : 'none')};
    }
  }

  @media screen and (max-width: 1070px) {
    flex-direction: ${({ isEditMode }) => (isEditMode ? 'row' : 'column')};
    gap: 12px;
    align-items: stretch;
  }
`
